import { useState } from 'react'

import {
  useCaseAccountTypes,
  useCaseConfigIsLoaded,
  useCaseTypes,
  useFetchCaseConfig,
} from '@/modules/projects/hooks/caseConfig'
import { useProject } from '@/modules/projects/hooks/project'
import {
  type CaseAnswers,
  caseTypeFields,
  DEFAULT_CASE_ACCOUNTS_TYPE,
  DEFAULT_CASE_TYPE,
  resolveCaseHeadline,
} from '@/modules/projects/stores/caseConfig'
import type { CaseAnswersState } from './types'

// Holds the case answers being edited. It never saves; the wizard and the edit
// dialog wrap it and decide when to write.
export const useCaseAnswers = (): CaseAnswersState => {
  const project = useProject()
  useFetchCaseConfig()
  const isLoaded = useCaseConfigIsLoaded()
  const caseTypes = useCaseTypes()
  const accountTypes = useCaseAccountTypes()

  const [caseType, setCaseType] = useState(
    project?.caseType ?? DEFAULT_CASE_TYPE,
  )
  const [details, setDetails] = useState<Record<string, string>>(
    project?.caseDetails ?? {},
  )
  const [accountsType, setAccountsType] = useState(
    project?.caseAccountsType ?? DEFAULT_CASE_ACCOUNTS_TYPE,
  )

  const values: CaseAnswers = {
    caseType,
    caseDetails: details,
    caseAccountsType: accountsType,
  }

  const changeCaseType = (value: string): CaseAnswers => {
    setCaseType(value)
    setDetails({})
    return { ...values, caseType: value, caseDetails: {} }
  }

  const changeDetail = (key: string, value: string): CaseAnswers => {
    setDetails((current) => ({ ...current, [key]: value }))
    return { ...values, caseDetails: { ...details, [key]: value } }
  }

  const changeAccountsType = (value: string): CaseAnswers => {
    setAccountsType(value)
    return { ...values, caseAccountsType: value }
  }

  const { caseTypeLabel, accountTypeLabel } = resolveCaseHeadline(values, {
    caseTypes,
    accountTypes,
  })

  return {
    form: {
      isLoaded,
      caseType,
      details,
      accountsType,
      caseTypes,
      accountTypes,
      fields: caseTypeFields(caseTypes, caseType),
      caseTypeLabel,
      accountTypeLabel,
    },
    values,
    changeCaseType,
    changeDetail,
    changeAccountsType,
  }
}
